import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { getRdv } from '../services/api';
import { useTranslation } from 'react-i18next';

const MapRdv = () => {
  const { t } = useTranslation();

  const [rdvs, setRdvs] = useState([]);
  const [filteredRdvs, setFilteredRdvs] = useState([]);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  // États des filtres
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterEquipe, setFilterEquipe] = useState('');
  const [filterCp, setFilterCp] = useState('');
  const [filterNom, setFilterNom] = useState('');
  const [filterOrigine, setFilterOrigine] = useState('');
  const [filterFiliale, setFilterFiliale] = useState('');

  // Options pour les picklists
  const [origineOptions, setOrigineOptions] = useState([]);
  const [filialeOptions, setFilialeOptions] = useState([]);
  const [equipeOptions, setEquipeOptions] = useState([]);

  // Configuration de la carte
  const mapContainerStyle = {
    width: '100%',
    height: 'calc(100vh - 200px)',
  };

  const center = {
    lat: 40.4637, // Centre de l'Espagne
    lng: -3.7492,
  };

  // Fonction pour récupérer les RDV et géocoder les adresses
  const fetchRdvs = async () => {
    const data = await getRdv();
    setRdvs(data);
    
    // Extraire et trier alphabétiquement pour Origine et Filiale
    const origines = [...new Set(data.map(rdv => rdv.Origine).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
    const filiales = [...new Set(data.map(rdv => rdv.filiale).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
    const equipes = [...new Set(data.map(rdv => rdv.equipe).filter(Boolean))]
      .sort((a, b) => {
        // Extraire les numéros des équipes
        const numA = parseInt(a.replace('EQUIPE ', ''));
        const numB = parseInt(b.replace('EQUIPE ', ''));
        return numA - numB;
      });
    
    setOrigineOptions(origines);
    setFilialeOptions(filiales);
    setEquipeOptions(equipes);

    // Fonction de géocodage qui utilise l'API Google Geocoder
    const geocodeAddress = (address) => {
      return new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve(results[0].geometry.location);
          } else {
            console.error("Erreur de géocodage pour l'adresse:", address, status);
            reject(status);
          }
        });
      });
    };

    // Géocoder chaque RDV pour obtenir la position exacte
    const rdvsWithCoordinates = await Promise.all(
      data.map(async (rdv) => {
        try {
          const location = await geocodeAddress(rdv.address);
          return {
            ...rdv,
            position: {
              lat: location.lat(),
              lng: location.lng()
            }
          };
        } catch (error) {
          // En cas d'erreur, on affecte la position par défaut (centre de l'Espagne)
          return {
            ...rdv,
            position: center
          };
        }
      })
    );

    setRdvs(rdvsWithCoordinates);
  };

  // Lancer le géocodage une fois que l'API Google Maps est chargée
  useEffect(() => {
    if (mapLoaded && window.google && window.google.maps) {
      fetchRdvs();
    }
  }, [mapLoaded]);

  // Filtrer les RDV selon les critères de recherche
  useEffect(() => {
    const filtered = rdvs.filter(rdv => {
      const matchDate = filterDate ? rdv.rdv_date === filterDate : true;
      const matchEquipe = filterEquipe ? rdv.equipe === filterEquipe : true;
      const matchCp = filterCp ? rdv.cp.includes(filterCp) : true;
      const fullName = `${rdv.Nom || ''} ${rdv.Prenom || ''}`.toLowerCase();
      const matchNom = filterNom ? fullName.includes(filterNom.toLowerCase()) : true;
      const matchOrigine = filterOrigine ? rdv.Origine === filterOrigine : true;
      const matchFiliale = filterFiliale ? rdv.filiale === filterFiliale : true;
      return matchDate && matchEquipe && matchCp && matchNom && matchOrigine && matchFiliale;
    });
    setFilteredRdvs(filtered);
  }, [rdvs, filterDate, filterEquipe, filterCp, filterNom, filterOrigine, filterFiliale]);

  // Gestion du clic sur un marqueur pour afficher l'InfoWindow
  const handleMarkerClick = (rdv) => {
    setSelectedRdv(rdv);
    setInfoWindowOpen(true);
  };

  // Ouvrir la boîte de dialogue avec plus de détails
  const handleShowDetails = () => {
    setInfoWindowOpen(false);
    setDialogOpen(true);
  };

  // Réinitialiser les filtres (la date sera remise à aujourd'hui)
  const handleResetFilters = () => {
    setFilterDate(new Date().toISOString().split('T')[0]);
    setFilterEquipe('');
    setFilterCp('');
    setFilterNom('');
    setFilterOrigine('');
    setFilterFiliale('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('mapRdv.title', { defaultValue: "Carte des Rendez-vous" })}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label={t('filters.date')}
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="equipe-select-label">{t('filters.team')}</InputLabel>
            <Select
              labelId="equipe-select-label"
              id="equipe-select"
              value={filterEquipe}
              label={t('filters.team')}
              onChange={(e) => setFilterEquipe(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('filters.team')}</em>
              </MenuItem>
              {equipeOptions.map(equipe => (
                <MenuItem key={equipe} value={equipe}>
                  {equipe}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('filters.department')}
            value={filterCp}
            onChange={(e) => setFilterCp(e.target.value)}
            size="small"
          />
          <TextField
            label={t('filters.name')}
            value={filterNom}
            onChange={(e) => setFilterNom(e.target.value)}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="origine-select-label">{t('filters.origin')}</InputLabel>
            <Select
              labelId="origine-select-label"
              id="origine-select"
              value={filterOrigine}
              label={t('filters.origin')}
              onChange={(e) => setFilterOrigine(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('filters.origin')}</em>
              </MenuItem>
              {origineOptions.map((origine) => (
                <MenuItem key={origine} value={origine}>
                  {origine}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="filiale-select-label">{t('filters.branch')}</InputLabel>
            <Select
              labelId="filiale-select-label"
              id="filiale-select"
              value={filterFiliale}
              label={t('filters.branch')}
              onChange={(e) => setFilterFiliale(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('filters.branch')}</em>
              </MenuItem>
              {filialeOptions.map((filiale) => (
                <MenuItem key={filiale} value={filiale}>
                  {filiale}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={handleResetFilters}>
            {t('filters.reset')}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ height: 'calc(100vh - 200px)' }}>
        <LoadScript
          googleMapsApiKey="AIzaSyBDxjT-tqx2etydNtgDJP6oLQkdKcZ3EaY"
          onLoad={() => setMapLoaded(true)}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={6}
          >
            {filteredRdvs.map((rdv, index) => (
              <Marker
                key={index}
                position={rdv.position}
                onClick={() => handleMarkerClick(rdv)}
              />
            ))}

            {selectedRdv && infoWindowOpen && (
              <InfoWindow
                position={selectedRdv.position}
                onCloseClick={() => setInfoWindowOpen(false)}
              >
                <Box>
                  <Typography variant="h6">
                    {selectedRdv.Nom} {selectedRdv.Prenom}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('infoWindow.date', { defaultValue: "Date:" })}</strong> {selectedRdv.rdv_date} {t('infoWindow.at', { defaultValue: "à" })} {selectedRdv.rdv_time}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('infoWindow.address', { defaultValue: "Adresse:" })}</strong> {selectedRdv.address}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('infoWindow.postalCode', { defaultValue: "CP:" })}</strong> {selectedRdv.cp}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('infoWindow.status', { defaultValue: "Statut:" })}</strong> {selectedRdv.status}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" onClick={handleShowDetails}>
                      {t('infoWindow.details', { defaultValue: "Plus de détails" })}
                    </Button>
                  </Box>
                </Box>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </Paper>

      {/* Boîte de dialogue détaillée */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('dialog.detailsTitle', { defaultValue: "Détails du Rendez-vous" })}</DialogTitle>
        <DialogContent dividers>
          {selectedRdv && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.client', { defaultValue: "Client" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.Nom} {selectedRdv.Prenom}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.dateTime', { defaultValue: "Date et heure" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.rdv_date} {t('dialog.at', { defaultValue: "à" })} {selectedRdv.rdv_time}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.address', { defaultValue: "Adresse" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.address}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.postalCode', { defaultValue: "Code Postal" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.cp}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.status', { defaultValue: "Statut" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.status}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.team', { defaultValue: "Équipe" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.equipe || "-"}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.surface', { defaultValue: "Surface" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.surface || "-"}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.estimatedSurface', { defaultValue: "Surface estimée" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.surface_estime || "-"}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.installedSurface', { defaultValue: "Surface installée" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.surface_installe || "-"}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.origin', { defaultValue: "Origine" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.Origine || "-"}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.installationType', { defaultValue: "Type d'installation" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.type_install || "-"}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  {t('dialog.branch', { defaultValue: "Filiale" })}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRdv.filiale || "-"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRdv?.leadid && (
            <Button
              variant="contained"
              color="primary"
              href={`https://kortex.phone.gs/vtigercrm/index.php?module=Leads&view=Detail&record=${selectedRdv.leadid}`}
              target="_blank"
            >
              {t('dialog.accessFile', { defaultValue: "Accès fiche" })}
            </Button>
          )}
          <Button onClick={() => setDialogOpen(false)} color="primary">
            {t('dialog.close', { defaultValue: "Fermer" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MapRdv;
